// Actual to Budget — the project-FK companion to the Dashboard. Re-groups
// the FY by PROJECT (Area → Subgroup → project-row, plus area-direct rows and
// synthetic "SAP Entries Without Projects" / "Budget Lines Without Projects" /
// Split-Batch areas), with a 3-measure display per column group: Budget /
// Actual / Variance, where Actual folds in committed (the committed sub-figure
// shows in a tooltip + secondary line).
//
// Consumes GET /api/budget/actual-to-budget/ (rebuild-then-read). The page
// shell bumps `refreshKey` when a budget.actual_to_budget_updated SSE event
// carries a new hash for the selected FY. All per-category sub-tabs ship in
// the single payload, so the segmented control filters client-side with no
// refetch (design §3/§4). The server emits WHOLE-DOLLAR strings (each cell
// rounded once from its own cents accumulator); the client renders them
// verbatim and NEVER re-sums months → quarter/annual (design §7.1).
//
// Rows carry `project_id` (the join key) for project-keyed rows, and
// `sap_transaction_id` for synthetic per-tx SAP-without-project rows.
// Subgroups (drag-drop, project-FK membership) use @hello-pangea/dnd: a
// project row drags into a subgroup → assignSubgroupMember(sgId, project_id) +
// refresh. Synthetic-area rows are never draggable. A SAP-without-project row
// is attributed to an existing project via "Update SAP Record"; a budgeted
// row's "Rename…" sets its display_alias + project_id (PATCH /lines/{id}/
// alias/). Destructive actions use an in-app confirm — never a native alert/
// confirm/prompt (they freeze the test browser, design §4).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { TOKENS, MONO_FONT } from '../reconciliation/tokens';
import { Icon, Tone, Modal, styles } from '../reconciliation/primitives';
import CellDrilldownDrawer from './CellDrilldownDrawer';
import AliasPopover from './AliasPopover';
import {
  fetchActualToBudget,
  fetchForecast,
  saveForecast,
  createSubgroup,
  updateSubgroup,
  deleteSubgroup,
  assignSubgroupMember,
  removeSubgroupMember,
} from './api';

// The three visible measures. Actual already folds committed in server-side
// for the displayed figure — we surface committed separately as a secondary
// line + tooltip so the fold is legible, not hidden (design §4).
const MEASURES = [
  { key: 'budget', label: 'Bud' },
  { key: 'actual', label: 'Act' },
  { key: 'variance', label: 'Var' },
];

// Feb-start fiscal calendar: P1 = Feb … P12 = Jan (mirrors
// src/budget/domain/fiscal.py — fiscal_year_bounds(FY) = Feb 1 FY .. Jan 31
// FY+1). Same array ForecastView uses; we add a calendar-year suffix per
// month so the modal reads "Feb '26 … Dec '26, Jan '27".
const PERIOD_MONTHS = ['Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec', 'Jan'];

// The four fiscal quarters, each three consecutive periods. P1-3 = Q1
// (Feb/Mar/Apr), P4-6 = Q2, P7-9 = Q3, P10-12 = Q4 — matches
// fiscal.py::quarter_for_period and rowQuarterTotal below.
const QUARTERS = [
  { q: 1, periods: [1, 2, 3] },
  { q: 2, periods: [4, 5, 6] },
  { q: 3, periods: [7, 8, 9] },
  { q: 4, periods: [10, 11, 12] },
];

// Per-period label with calendar-year suffix for the edit-budget modal.
// FY N spans Feb N .. Jan N+1, so periods 1–11 (Feb..Dec) carry year N and
// period 12 (Jan) carries year N+1. Derived from `fiscalYear` — not hardcoded.
// e.g. monthLabel(2026, 1) = "Feb '26", monthLabel(2026, 12) = "Jan '27".
function monthLabel(fiscalYear, period) {
  const calYear = period === 12 ? fiscalYear + 1 : fiscalYear;
  const yy = String(calYear % 100).padStart(2, '0');
  return `${PERIOD_MONTHS[period - 1]} '${yy}`;
}

// Excel-style money for the read grid: thousands separators, WHOLE DOLLARS
// (no decimals), parens for negatives. The server already rounds each cell to
// whole dollars from its own cents accumulator and ships it as a string, so
// the client renders verbatim and never re-sums (design §7.1).
function fmtAmount(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const s = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
  return n < 0 ? `(${s})` : s;
}

// Cent-precision variant for the budget-edit modal's LIVE per-quarter / FY
// totals only — an editing aid summed over the user's typed values, not a
// projection cell, so it keeps decimals while editing (design §7.1).
function fmtAmountCents(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return '—';
  const s = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `(${s})` : s;
}

function fmtDate(s) {
  if (!s) return '';
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US');
}

// Resolve the {budget, actual, committed, variance} cell for a column group
// on any node shape: rows/subtotals/totals all carry {annual:{...}, quarters}.
function cellFor(node, group) {
  if (!node) return {};
  if (group.kind === 'annual') return node.annual || node;
  return (node.quarters || {})[group.key] || {};
}

// One column group's three cells for a node. Per-measure click behavior on a
// row line (subtotal/area/grand rows pass neither handler, so nothing clicks):
//   - Bud cell  → onOpenBudget() (the vendor budget-edit modal). Available on
//     EVERY column incl. annual — the modal always edits the full FY grid.
//   - Act / Var → onOpenDrill() (the name-scoped drill-down drawer). Available
//     on every column INCLUDING annual (Dashboard parity); the A2B drill is
//     FY-wide / name-scoped, so the column choice doesn't change what it shows.
// Actual cell shows actual+committed (server already summed them into
// `actual`); the committed sub-figure renders as a secondary line + tooltip.
// Variance > 0 (over budget) paints red.
function MeasureCells({ node, group, onOpenDrill, onOpenBudget, annualEnd }) {
  const cell = cellFor(node, group);
  return MEASURES.map(({ key }, i) => {
    const over = key === 'variance' && Number(cell.variance || 0) > 0;
    const isBudget = key === 'budget';
    const clickable = isBudget ? Boolean(onOpenBudget) : Boolean(onOpenDrill);
    const onClick = isBudget ? onOpenBudget : onOpenDrill;
    const classes = ['bt-num'];
    if (i === 0) classes.push('group-edge');
    if (over) classes.push('var-over');
    if (clickable) classes.push('bt-click');
    if (annualEnd && i === MEASURES.length - 1) classes.push('annual-right-border');
    const committed = Number(cell.committed || 0);
    const showCommitted = key === 'actual' && committed !== 0;
    return (
      <td
        key={`${group.id}-${key}`}
        className={classes.join(' ')}
        onClick={clickable ? onClick : undefined}
        title={
          clickable
            ? isBudget
              ? 'Edit budget'
              : `Drill into ${group.label}`
            : showCommitted
              ? `Actual incl. ${fmtAmount(cell.committed)} committed`
              : undefined
        }
      >
        {fmtAmount(cell[key])}
        {showCommitted && (
          <span className="a2b-committed" title={`${fmtAmount(cell.committed)} committed`}>
            +{fmtAmount(cell.committed)} com
          </span>
        )}
      </td>
    );
  });
}

// `onOpenDrill` / `onOpenBudget` are passed only for clickable row lines.
// Subtotal / area / grand nodes call RowCells with neither, so their cells
// render inert.
function RowCells({ groups, node, onOpenDrill, onOpenBudget }) {
  return (
    <>
      {groups.map((g, gi) => (
        <MeasureCells
          key={g.id}
          node={node}
          group={g}
          onOpenDrill={onOpenDrill}
          onOpenBudget={onOpenBudget}
          annualEnd={gi === 0}
        />
      ))}
    </>
  );
}

// ── In-app confirm (no native confirm — design §4) ──────────────────────
function ConfirmInline({ label, onConfirm, onCancel, busy }) {
  return (
    <>
      <span className="a2b-confirm-q">{label}</span>
      <button style={styles.ghostBtn} disabled={busy} onClick={onConfirm}>
        Yes, delete
      </button>
      <button style={styles.ghostBtn} disabled={busy} onClick={onCancel}>
        Keep it
      </button>
    </>
  );
}

// ── Vendor-level budget-edit modal ───────────────────────────────────────
// Scoped to the clicked row's budget line. Prefilled from GET /forecast/,
// lets the user add/edit/remove vendor rows + override the source_tab, then
// POSTs the FULL FY grid via saveForecast (every active line, full-replace
// zeroing semantics — design §4). Surfaces returned warnings.
//
// ACTUAL-ONLY ROW (lineId == null): a project-attributed actual row with no
// backing budget line yet. We carry `projectId`/`lineName` and, on save,
// append a PROJECT-KEYED entry ({project_id, rows} — no budget_line_id) to the
// full-grid post. `save_forecast` then LAZILY creates/looks up the line by
// (fiscal_year, project_id) (ensure_line_for_project, design §4D) before
// writing amounts. Every existing line is still re-posted verbatim alongside
// it — the project-keyed entry is ADDED, never posted alone.
const TAB_OPTIONS = [
  { value: '', label: '(auto-derive)' },
  { value: 'Outside Spend', label: 'Outside Spend' },
  { value: 'Travel', label: 'Travel' },
  { value: 'Travel Other', label: 'Travel Other' },
  { value: 'Memberships & Dues', label: 'Membership, Dues, and Subscriptions' },
  { value: 'Temp Labor', label: 'Temp Labor' },
  { value: 'All Other', label: 'All Other' },
];

function emptyAmounts() {
  const a = {};
  for (let p = 1; p <= 12; p += 1) a[String(p)] = '0.00';
  return a;
}

function rowQuarterTotal(amounts, q) {
  // FY quarters map to periods: Q1=P1-3, Q2=P4-6, Q3=P7-9, Q4=P10-12.
  let sum = 0;
  for (let i = 0; i < 3; i += 1) {
    sum += Number(amounts[String((q - 1) * 3 + i + 1)] || 0);
  }
  return sum;
}

function rowAnnualTotal(amounts) {
  let sum = 0;
  for (let p = 1; p <= 12; p += 1) sum += Number(amounts[String(p)] || 0);
  return sum;
}

function VendorBudgetModal({ fiscalYear, lineId, lineName, projectId, onClose, onToast, onChanged }) {
  const [grid, setGrid] = useState(null); // full /forecast/ payload
  const [scopedRows, setScopedRows] = useState([]); // editable rows for THIS line
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [warnings, setWarnings] = useState([]);
  // An actual-only row has no backing budget line yet — the save will lazily
  // create one by project_id (design §4D). It will not be found in grid.lines.
  const isActualOnly = !lineId;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchForecast(fiscalYear)
      .then((res) => {
        if (cancelled) return;
        setGrid(res.data);
        const line = (res.data.lines || []).find((l) => l.id === lineId);
        const rows = (line?.rows || []).map((r) => ({
          vendor_id: r.vendor_id || null,
          // The free-text "Vendor / detail label" input is seeded from the
          // resolved vendor_name when a vendor_id is set, else from the
          // persisted detail_label. A label-only row (added free-text, no
          // vendor_id) comes back from /forecast/ with vendor_name=null and
          // detail_label=<text>; without this fallback the input rendered
          // blank on reopen and the label looked like it hadn't persisted.
          vendor_name: r.vendor_name || r.detail_label || '',
          detail_label: r.detail_label || '',
          detail_type: r.detail_type || 'standard',
          detail_meta: r.detail_meta || null,
          source_tab: r.source_tab || '',
          amounts: { ...emptyAmounts(), ...(r.amounts || {}) },
        }));
        setScopedRows(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Failed to load grid');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [fiscalYear, lineId]);

  const setRow = (index, patch) =>
    setScopedRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));

  const setAmount = (index, period, value) =>
    setScopedRows((rows) =>
      rows.map((r, i) =>
        i === index ? { ...r, amounts: { ...r.amounts, [String(period)]: value } } : r
      )
    );

  const addRow = () =>
    setScopedRows((rows) => [
      ...rows,
      {
        vendor_id: null,
        vendor_name: '',
        detail_label: '',
        detail_type: 'standard',
        detail_meta: null,
        source_tab: '',
        amounts: emptyAmounts(),
      },
    ]);

  const removeRow = (index) =>
    setScopedRows((rows) => rows.filter((_, i) => i !== index));

  // Serialize the edited (scoped) rows for the post.
  const scopedRowsPayload = () =>
    scopedRows.map((r) => ({
      vendor_id: r.vendor_id || null,
      detail_label: r.detail_label?.trim() || null,
      detail_type: r.detail_type || 'standard',
      detail_meta: r.detail_meta || null,
      // A blank override → omit source_tab so the server re-derives it;
      // an explicit choice is an EXACT manual override (B7).
      ...(r.source_tab ? { source_tab: r.source_tab } : {}),
      amounts: r.amounts,
    }));

  const handleSave = async () => {
    if (!grid) return;
    setBusy(true);
    setError(null);
    setWarnings([]);
    // CRITICAL (data-loss guard): the save is a FULL-FY REPLACE — an active
    // line omitted from the payload gets zeroed in the new snapshot. So we
    // post EVERY existing line. The scoped line's rows are the edited ones;
    // every OTHER line round-trips its grid rows verbatim so it is never
    // zeroed (design §4). Never post the edited/name-only entry alone.
    const lines = (grid.lines || []).map((l) => {
      if (!isActualOnly && l.id === lineId) {
        return { budget_line_id: l.id, rows: scopedRowsPayload() };
      }
      return {
        budget_line_id: l.id,
        rows: (l.rows || []).map((r) => ({
          vendor_id: r.vendor_id || null,
          detail_label: r.detail_label || null,
          detail_type: r.detail_type || 'standard',
          detail_meta: r.detail_meta || null,
          ...(r.source_tab ? { source_tab: r.source_tab } : {}),
          amounts: r.amounts,
        })),
      };
    });
    // ACTUAL-ONLY ROW: there is no existing line to replace, so APPEND a
    // project-keyed entry (no budget_line_id, carry the project_id). The
    // server lazily creates/looks up the line by (fiscal_year, project_id)
    // before writing amounts (design §4D). It is added ON TOP of the verbatim
    // full grid.
    if (isActualOnly) {
      lines.push({ project_id: projectId, rows: scopedRowsPayload() });
    }
    try {
      const res = await saveForecast(fiscalYear, `Edit “${lineName}” budget`, lines);
      const w = res.data?.warnings || [];
      if (w.length) {
        setWarnings(w);
        onToast({ text: `Saved with ${w.length} warning(s)`, tone: 'warn' });
      } else {
        onToast({ text: 'Budget saved', tone: 'good' });
      }
      onChanged();
      if (!w.length) onClose();
      else setBusy(false);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Save failed');
      setBusy(false);
    }
  };

  return (
    <Modal
      title={`Edit budget — ${lineName}`}
      subtitle={
        isActualOnly
          ? `FY${fiscalYear} · new budget line created on save for this project · full-FY replace`
          : `FY${fiscalYear} · full-FY replace · every active line is re-posted`
      }
      wide
      onClose={onClose}
      footer={
        <>
          <button className="fc-add-row" onClick={addRow}>
            + Add vendor row
          </button>
          <span style={{ marginLeft: 'auto' }} />
          <button style={styles.ghostBtn} onClick={onClose}>
            Cancel
          </button>
          <button style={styles.primaryBtn} disabled={busy || loading} onClick={handleSave}>
            {busy ? 'Saving…' : 'Save full FY grid'}
          </button>
        </>
      }
    >
      {loading ? (
        <div className="budget-empty">
          <div className="title">Loading forecast grid…</div>
        </div>
      ) : error ? (
        <div className="a2b-error">{error}</div>
      ) : (
        <div className="a2b-vendor-modal">
          {warnings.length > 0 && (
            <div className="a2b-warnings">
              {warnings.map((w, i) => (
                <div key={i} className="a2b-warning">
                  <Tone tone="soft">warning</Tone> {w}
                </div>
              ))}
            </div>
          )}
          {scopedRows.length === 0 && (
            <div className="a2b-empty-rows">
              No vendor rows yet — add one to budget this line.
            </div>
          )}
          {scopedRows.map((r, i) => (
            <div className="a2b-vendor-row" key={i}>
              <div className="a2b-vendor-head">
                <input
                  className="fc-text"
                  value={r.vendor_name}
                  placeholder="Vendor / detail label"
                  onChange={(e) => setRow(i, { vendor_name: e.target.value, detail_label: e.target.value })}
                />
                <select
                  className="fc-text a2b-tab-select"
                  value={r.source_tab}
                  title="Category override — an explicit choice is stamped exact"
                  onChange={(e) => setRow(i, { source_tab: e.target.value })}
                >
                  {TAB_OPTIONS.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
                <span className="a2b-vendor-total">
                  FY {fmtAmountCents(rowAnnualTotal(r.amounts))}
                </span>
                <button className="fc-remove" title="Remove row" onClick={() => removeRow(i)}>
                  <Icon d="M6 6l12 12M18 6L6 18" size={11} sw={2} />
                </button>
              </div>
              {/* All 12 fiscal months on ONE row, grouped into four shaded,
                  bordered, labeled quarter blocks (Q1 = Feb/Mar/Apr …). Each
                  block shows a LIVE per-quarter total that recomputes as its
                  month amounts are edited; the FY total in the row head
                  recomputes the same way. Month labels carry the calendar-year
                  suffix derived from the FY (fiscal.py Feb-start). */}
              <div className="a2b-quarter-grid">
                {QUARTERS.map(({ q, periods }) => (
                  <div key={q} className="a2b-quarter-block">
                    <div className="a2b-quarter-label">Q{q}</div>
                    <div className="a2b-quarter-months">
                      {periods.map((period) => (
                        <label key={period} className="a2b-period-cell">
                          <span className="a2b-period-lbl">{monthLabel(fiscalYear, period)}</span>
                          <input
                            className="fc-input a2b-month-input"
                            value={r.amounts[String(period)]}
                            onChange={(e) => setAmount(i, period, e.target.value)}
                          />
                        </label>
                      ))}
                    </div>
                    <div className="a2b-quarter-total">
                      Q{q} {fmtAmountCents(rowQuarterTotal(r.amounts, q))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// ── Subgroup editor (create / rename / delete + reorder by DnD) ──────────
function SubgroupManager({ areas, onCreate, onRename, onDelete, busy }) {
  const [creatingArea, setCreatingArea] = useState('');
  const [newName, setNewName] = useState('');
  const [renaming, setRenaming] = useState(null); // {id, name}
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const realAreas = areas.filter((a) => !a.synthetic);

  return (
    <div className="a2b-sg-manager">
      <div className="a2b-sg-create">
        <select
          className="fc-text"
          value={creatingArea}
          onChange={(e) => setCreatingArea(e.target.value)}
        >
          <option value="">Choose area…</option>
          {realAreas.map((a) => (
            <option key={a.area} value={a.area}>
              {a.area}
            </option>
          ))}
        </select>
        <input
          className="fc-text"
          value={newName}
          placeholder="New subgroup name"
          onChange={(e) => setNewName(e.target.value)}
        />
        <button
          style={styles.ghostBtn}
          disabled={busy || !creatingArea || !newName.trim()}
          onClick={() => {
            onCreate(creatingArea, newName.trim());
            setNewName('');
          }}
        >
          + Create subgroup
        </button>
      </div>
      <div className="a2b-sg-list">
        {realAreas.flatMap((a) =>
          (a.subgroups || []).map((sg) => (
            <div key={sg.id} className="a2b-sg-item">
              <span className="a2b-sg-area">{a.area}</span>
              {renaming?.id === sg.id ? (
                <>
                  <input
                    className="fc-text"
                    value={renaming.name}
                    onChange={(e) => setRenaming({ id: sg.id, name: e.target.value })}
                  />
                  <button
                    style={styles.ghostBtn}
                    disabled={busy || !renaming.name.trim()}
                    onClick={() => {
                      onRename(sg.id, renaming.name.trim());
                      setRenaming(null);
                    }}
                  >
                    Save
                  </button>
                  <button style={styles.ghostBtn} onClick={() => setRenaming(null)}>
                    Cancel
                  </button>
                </>
              ) : confirmDeleteId === sg.id ? (
                <ConfirmInline
                  label={`Delete “${sg.name}”? Members revert to area-direct.`}
                  busy={busy}
                  onConfirm={() => {
                    onDelete(sg.id);
                    setConfirmDeleteId(null);
                  }}
                  onCancel={() => setConfirmDeleteId(null)}
                />
              ) : (
                <>
                  <span className="a2b-sg-name">{sg.name}</span>
                  <button
                    className="fc-recalc"
                    onClick={() => setRenaming({ id: sg.id, name: sg.name })}
                  >
                    rename
                  </button>
                  <button className="fc-recalc" onClick={() => setConfirmDeleteId(sg.id)}>
                    delete
                  </button>
                </>
              )}
            </div>
          ))
        )}
        {realAreas.every((a) => !(a.subgroups || []).length) && (
          <div className="a2b-sg-empty">
            No subgroups yet. Create one, then drag project rows into it.
          </div>
        )}
      </div>
    </div>
  );
}

export default function ActualToBudgetView({ fiscalYear, refreshKey, onHash, onToast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
  const [drill, setDrill] = useState(null);
  const [aliasRow, setAliasRow] = useState(null);
  const [budgetEdit, setBudgetEdit] = useState(null);
  const [showSubgroups, setShowSubgroups] = useState(false);
  const [sgBusy, setSgBusy] = useState(false);
  // Local refresh bump (belt-and-suspenders for a dropped SSE connection).
  const [localKey, setLocalKey] = useState(0);
  const bump = useCallback(() => setLocalKey((k) => k + 1), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchActualToBudget(fiscalYear);
      setData(res.data);
      if (onHash) onHash(res.data?.projection_hash || null);
    } catch (err) {
      if (err.response?.status === 404) {
        setData(null);
      } else {
        setError(err.response?.data?.detail || err.message || 'Failed to load Actual to Budget');
        setData(null);
      }
    } finally {
      setLoading(false);
    }
  }, [fiscalYear, onHash]);

  useEffect(() => {
    load();
  }, [load, refreshKey, localKey]);

  // Column groups: Annual first (Excel order), then Q1..Q4.
  const groups = useMemo(() => {
    const out = [{ id: 'annual', label: 'Annual', kind: 'annual' }];
    for (let q = 1; q <= 4; q += 1) {
      out.push({ id: `q${q}`, label: `Q${q}`, kind: 'quarter', key: String(q) });
    }
    return out;
  }, []);

  const toggle = (key) => {
    setCollapsedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Client-side sub-tab filter: when a category tab is active, a row is kept
  // only if its `source_tabs` includes that tab. Synthetic-area rows (no
  // budget tag) are kept only on "All". Display-only — never re-sums.
  const tabMatches = useCallback(
    (row) => {
      if (activeTab === 'all') return true;
      return (row.source_tabs || []).includes(activeTab);
    },
    [activeTab]
  );

  // A2B actuals are keyed by the row's effective PROJECT-FK (`project_id`), or
  // a single SAP tx (`sap_transaction_id`) for a synthetic SAP-without-project
  // row — NOT by budget_line_id like the Dashboard. The drawer hits the
  // project-scoped drilldown (§6) to show the SAP rows + committed invoices
  // behind the row's actual side. A row is drillable if it carries either
  // identity; synthetic SPLIT_BATCH rows carry neither and are not drillable.
  const rowDrillable = (row) =>
    Boolean(row.project_id) || Boolean(row.sap_transaction_id);

  const openDrill = (row) => {
    if (!rowDrillable(row)) return;
    setDrill({
      a2b: true,
      // Prefer the project key; fall back to the per-tx key for synthetic SAP
      // rows (which have no project_id).
      projectId: row.project_id || undefined,
      sapTransactionId: row.project_id ? undefined : row.sap_transaction_id || undefined,
      lineName: row.name,
      fiscalYear,
    });
  };

  const openBudgetEdit = (row) =>
    setBudgetEdit({
      lineId: row.budget_line_id || null,
      lineName: row.name,
      projectId: row.project_id || null,
    });

  // ── Subgroup mutations ─────────────────────────────────────────────────
  const handleCreate = async (area, name) => {
    setSgBusy(true);
    try {
      await createSubgroup(fiscalYear, area, name);
      onToast({ text: `Subgroup “${name}” created`, tone: 'good' });
      bump();
    } catch (err) {
      onToast({ text: err.response?.data?.detail || err.message || 'Create failed', tone: 'miss' });
    } finally {
      setSgBusy(false);
    }
  };

  const handleRename = async (id, name) => {
    setSgBusy(true);
    try {
      await updateSubgroup(id, name);
      onToast({ text: 'Subgroup renamed', tone: 'good' });
      bump();
    } catch (err) {
      onToast({ text: err.response?.data?.detail || err.message || 'Rename failed', tone: 'miss' });
    } finally {
      setSgBusy(false);
    }
  };

  const handleDelete = async (id) => {
    setSgBusy(true);
    try {
      await deleteSubgroup(id);
      onToast({ text: 'Subgroup deleted', tone: 'good' });
      bump();
    } catch (err) {
      onToast({ text: err.response?.data?.detail || err.message || 'Delete failed', tone: 'miss' });
    } finally {
      setSgBusy(false);
    }
  };

  // DnD: dragging a project row between subgroups (or to area-direct).
  // droppableId encodes the target: `sg:<id>` for a subgroup, `area:<name>`
  // for area-direct. draggableId is `proj:<project_id>` (only project-keyed
  // rows are draggable). Membership keys by project_id (design §5/§6). A
  // project_id is a UUID (no embedded colon), so the remainder after the
  // fixed-length `proj:` prefix is the verbatim FK.
  const onDragEnd = async (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;
    const projectId = draggableId.startsWith('proj:') ? draggableId.slice(5) : null;
    if (!projectId) return;
    try {
      // Remove from a source subgroup if it had one.
      if (source.droppableId.startsWith('sg:')) {
        await removeSubgroupMember(source.droppableId.slice(3), projectId);
      }
      // Add to the destination subgroup (area-direct = no membership).
      if (destination.droppableId.startsWith('sg:')) {
        await assignSubgroupMember(destination.droppableId.slice(3), projectId);
      }
      onToast({ text: 'Subgroup membership updated', tone: 'good' });
      bump();
    } catch (err) {
      onToast({
        text: err.response?.data?.detail || err.message || 'Could not move row',
        tone: 'miss',
      });
      bump();
    }
  };

  if (loading && !data) {
    return (
      <div className="budget-empty">
        <div className="title">Loading Actual to Budget…</div>
        <div className="sub">Rebuilding the FY{fiscalYear} project-keyed projection.</div>
      </div>
    );
  }
  if (error && !data) {
    return (
      <div className="budget-empty">
        <div className="title">Couldn't load Actual to Budget</div>
        <div className="sub">{error}</div>
      </div>
    );
  }
  if (!data || !(data.areas || []).length) {
    return (
      <div className="budget-empty">
        <div className="title">No data for FY{fiscalYear}</div>
        <div className="sub">
          Import the FY workbook and reconcile SAP rows to populate this view.
        </div>
      </div>
    );
  }

  const allHeuristic = Boolean(data.source_tab_all_heuristic);
  const sourceTabs = data.source_tabs || [];
  const colCount = 1 + groups.length * MEASURES.length;

  // A column-span placeholder so the DnD <Droppable> can wrap a <tbody>
  // without breaking table semantics.
  const renderRow = (row, depth, indexInDrop) => {
    // A project-keyed row (project_id present) is draggable, drillable, and
    // budget-editable, and its budgeted line (if any) is renamable. A
    // synthetic per-tx SAP-without-project row (sap_transaction_id, no
    // project_id) is attributable ("Update SAP Record") + drillable but NOT
    // draggable/budget-editable (no project yet to key a subgroup membership
    // or lazily create a line on — the user attributes it first). A
    // SPLIT_BATCH row carries neither identity.
    const hasProject = Boolean(row.project_id);
    const isSapAttributable = Boolean(row.sap_transaction_id);
    const draggable = hasProject;
    // The pencil opens AliasPopover: "Rename…" for a budgeted row, or "Update
    // SAP Record" for a SAP-without-project row.
    const aliasable = Boolean(row.budget_line_id) || isSapAttributable;
    const budgetEditable = hasProject;
    // Stable React key for the mapped <tr>. Every row carries at least one
    // identity (project_id / sap_transaction_id / budget_line_id); fall back to
    // area+name+index so synthetic-area rows never collide. Without this the
    // non-draggable <tr> path below mounts keyless and React warns on every
    // render (and may mis-reconcile rows on reorder).
    const rowKey =
      (hasProject && `proj:${row.project_id}`) ||
      (row.sap_transaction_id && `sap:${row.sap_transaction_id}`) ||
      (row.budget_line_id && `line:${row.budget_line_id}`) ||
      `row:${row.area}:${row.name}:${indexInDrop}`;
    const inner = (dragProps, dragHandle, isDragging) => (
      <tr
        className={`row-line${isDragging ? ' a2b-dragging' : ''}`}
        ref={dragProps?.innerRef}
        {...(dragProps?.draggableProps || {})}
      >
        <td className="bt-name" style={{ paddingLeft: 28 + depth * 18 }}>
          {draggable && (
            <span className="a2b-drag-handle" {...(dragHandle || {})} title="Drag into a subgroup">
              <Icon d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" size={12} sw={2} />
            </span>
          )}
          <span className="a2b-row-name">{row.name}</span>
          {row.needs_fx > 0 && (
            <Tone tone="soft" title="Some amounts await an FX rate">
              needs FX {row.needs_fx}
            </Tone>
          )}
          {aliasable && (
            <button
              className="a2b-pencil"
              title={isSapAttributable ? 'Update SAP Record' : 'Rename (display name + project)'}
              onClick={() => setAliasRow(row)}
            >
              <Icon d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" size={11} sw={1.8} />
            </button>
          )}
          {budgetEditable && (
            <button
              className="a2b-pencil"
              title={row.budget_line_id ? 'Edit vendor budget' : 'Add budget (creates a line)'}
              onClick={() => openBudgetEdit(row)}
            >
              <Icon d="M3 3h18v4H3zM3 10h18v4H3zM3 17h18v4H3z" size={11} sw={1.6} />
            </button>
          )}
        </td>
        <RowCells
          groups={groups}
          node={row}
          // Act/Var cells (incl. annual) → name-scoped drill-down; Bud cells
          // (incl. annual) → the vendor budget-edit modal (lazy-creates a line
          // for an actual-only row). Handlers passed only when the row supports
          // the action (PO acceptance criteria).
          onOpenDrill={rowDrillable(row) ? () => openDrill(row) : undefined}
          onOpenBudget={budgetEditable ? () => openBudgetEdit(row) : undefined}
        />
      </tr>
    );
    if (!draggable) return <React.Fragment key={rowKey}>{inner()}</React.Fragment>;
    return (
      <Draggable draggableId={`proj:${row.project_id}`} index={indexInDrop} key={rowKey}>
        {(prov, snap) => inner(prov, prov.dragHandleProps, snap.isDragging)}
      </Draggable>
    );
  };

  return (
    <>
      {data.snapshot && (
        <div className="a2b-reconciling">
          <span className="snap">
            snapshot “{data.snapshot.reason || 'unnamed'}” · {fmtDate(data.snapshot.created_at)}
          </span>
        </div>
      )}

      {/* Sub-tab segmented control + subgroup toggle. */}
      <div className="budget-toolbar a2b-toolbar">
        <div className="budget-seg a2b-seg">
          <button className={activeTab === 'all' ? 'on' : ''} onClick={() => setActiveTab('all')}>
            All
          </button>
          {sourceTabs.map((t) => (
            <button
              key={t.tab}
              className={activeTab === t.tab ? 'on' : ''}
              onClick={() => setActiveTab(t.tab)}
              title={t.confidence === 'heuristic' ? 'Category split is best-effort' : undefined}
            >
              {t.label}
              {(allHeuristic || t.confidence === 'heuristic') && (
                <span className="a2b-approx" title="Approximate — historical split is best-effort">
                  approx.
                </span>
              )}
            </button>
          ))}
        </div>
        {allHeuristic && (
          <span className="a2b-approx-banner" title="This FY predates exact category tagging">
            FY category splits are approximate
          </span>
        )}
        <span
          style={{
            alignSelf: 'center',
            fontSize: 11.5,
            fontWeight: 600,
            color: TOKENS.ink2,
          }}
        >
          Subgroups:
        </span>
        <button
          style={styles.ghostBtn}
          onClick={() => setShowSubgroups((v) => !v)}
        >
          <Icon d="M3 3h18v4H3zM3 10h18v4H3zM3 17h18v4H3z" size={11} sw={1.6} />
          {showSubgroups ? 'Hide subgroups' : 'Manage subgroups'}
        </button>
      </div>

      {showSubgroups && (
        <SubgroupManager
          areas={data.areas}
          busy={sgBusy}
          onCreate={handleCreate}
          onRename={handleRename}
          onDelete={handleDelete}
        />
      )}

      <DragDropContext onDragEnd={onDragEnd}>
        <div className="budget-table-wrap budget-dashboard-wrap">
          <table className="budget-table a2b-table">
            <thead>
              <tr>
                <th className="bt-name" rowSpan={2}>
                  Project
                </th>
                {groups.map((g, gi) => (
                  <th
                    key={g.id}
                    colSpan={MEASURES.length}
                    className={['group-edge', gi === 0 ? 'annual-right-border' : '']
                      .filter(Boolean)
                      .join(' ')}
                    style={{ textAlign: 'center' }}
                  >
                    {g.label}
                  </th>
                ))}
              </tr>
              <tr>
                {groups.map((g, gi) =>
                  MEASURES.map((m, i) => (
                    <th
                      key={`${g.id}-${m.key}`}
                      className={[
                        i === 0 ? 'group-edge' : '',
                        gi === 0 && i === MEASURES.length - 1 ? 'annual-right-border' : '',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      {m.label}
                    </th>
                  ))
                )}
              </tr>
            </thead>
            {/* Each area emits sibling <tbody> blocks: a header tbody, then
                one droppable tbody per subgroup, then a droppable tbody for
                area-direct rows. (A <tbody> cannot nest, and @hello-pangea/dnd
                makes each droppable its own tbody.) */}
            {data.areas.map((area) => {
              const areaKey = `a:${area.area}`;
              const areaCollapsed = collapsedKeys.has(areaKey);
              const directRows = (area.rows || []).filter(tabMatches);
              const subgroups = (area.subgroups || []).map((sg) => ({
                ...sg,
                rows: (sg.rows || []).filter(tabMatches),
              }));
              return (
                <React.Fragment key={areaKey}>
                  <tbody>
                    <tr className="row-owner">
                      <td className="bt-name">
                        <button className="bt-twist" onClick={() => toggle(areaKey)}>
                          <Icon
                            d={areaCollapsed ? 'M9 6l6 6-6 6' : 'M6 9l6 6 6-6'}
                            size={11}
                            sw={2}
                          />
                        </button>
                        {area.area}
                      </td>
                      <RowCells groups={groups} node={area.subtotals || {}} />
                    </tr>
                  </tbody>
                  {!areaCollapsed &&
                    subgroups.map((sg) => {
                      const sgKey = `${areaKey}/sg:${sg.id}`;
                      const sgCollapsed = collapsedKeys.has(sgKey);
                      return (
                        <Droppable droppableId={`sg:${sg.id}`} key={sgKey}>
                          {(prov) => (
                            <tbody
                              className="a2b-droppable"
                              ref={prov.innerRef}
                              {...prov.droppableProps}
                            >
                              <tr className="row-category a2b-sg-row">
                                <td className="bt-name" style={{ paddingLeft: 26 }}>
                                  <button className="bt-twist" onClick={() => toggle(sgKey)}>
                                    <Icon
                                      d={sgCollapsed ? 'M9 6l6 6-6 6' : 'M6 9l6 6 6-6'}
                                      size={11}
                                      sw={2}
                                    />
                                  </button>
                                  {sg.name}
                                  <span className="a2b-sg-count">{(sg.rows || []).length}</span>
                                </td>
                                <RowCells groups={groups} node={sg.subtotals || {}} />
                              </tr>
                              {!sgCollapsed &&
                                (sg.rows || []).map((row, idx) => renderRow(row, 2, idx))}
                              {!sgCollapsed && !(sg.rows || []).length && (
                                <tr className="a2b-drop-hint">
                                  <td colSpan={colCount} style={{ paddingLeft: 62 }}>
                                    Drag a project row here
                                  </td>
                                </tr>
                              )}
                              {prov.placeholder}
                            </tbody>
                          )}
                        </Droppable>
                      );
                    })}
                  {/* Area-direct rows (synthetic rows + ungrouped projects). */}
                  {!areaCollapsed && (
                    <Droppable droppableId={`area:${area.area}`}>
                      {(prov) => (
                        <tbody
                          className="a2b-droppable"
                          ref={prov.innerRef}
                          {...prov.droppableProps}
                        >
                          {directRows.map((row, idx) => renderRow(row, 1, idx))}
                          {prov.placeholder}
                        </tbody>
                      )}
                    </Droppable>
                  )}
                </React.Fragment>
              );
            })}
            <tbody>
              <tr className="row-grand">
                <td className="bt-name">
                  Grand total ·{' '}
                  <span style={{ fontFamily: MONO_FONT, fontSize: 10.5, color: TOKENS.ink2 }}>
                    FY{fiscalYear}
                  </span>
                </td>
                <RowCells groups={groups} node={data.totals || {}} />
              </tr>
            </tbody>
          </table>
        </div>
      </DragDropContext>

      {drill && (
        <CellDrilldownDrawer
          cell={drill}
          onClose={() => setDrill(null)}
          onToast={onToast}
          onChanged={bump}
        />
      )}
      {aliasRow && (
        <AliasPopover
          row={aliasRow}
          onClose={() => setAliasRow(null)}
          onToast={onToast}
          onChanged={bump}
        />
      )}
      {budgetEdit && (
        <VendorBudgetModal
          fiscalYear={fiscalYear}
          lineId={budgetEdit.lineId}
          lineName={budgetEdit.lineName}
          projectId={budgetEdit.projectId}
          onClose={() => setBudgetEdit(null)}
          onToast={onToast}
          onChanged={bump}
        />
      )}
    </>
  );
}
